import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import {
  applyLightThemeToCloneDocument,
  DOCUMENT_EXPORT_CAPTURE_SETTLE_MS,
  injectStylesIntoCloneDocument,
  moveHtml2canvasCloneOffScreen,
  stripDocumentExportStylesheetsFromClone,
} from './documentExportLightTheme';
import { printStandaloneHtmlDocument } from './modalDocumentExport';

const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const SHEET_CANVAS_CAPTURE_WIDTH_PX = 794;
const SHEET_CANVAS_CAPTURE_HEIGHT_PX = Math.round((SHEET_CANVAS_CAPTURE_WIDTH_PX * 297) / 210);

const EVALUATION_PLAN_SHEET_CANVAS_PRINT_CSS = `
  @page {
    size: ${A4_WIDTH_MM}mm ${A4_HEIGHT_MM}mm;
    margin: 0;
  }
  html,
  body {
    margin: 0;
    padding: 0;
    background: #ffffff;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .cp-evaluation-plan-sheet-canvas-print-port {
    width: ${SHEET_CANVAS_CAPTURE_WIDTH_PX}px;
    margin: 0;
    display: block;
    box-sizing: border-box;
  }
  .cp-evaluation-plan-sheet-canvas-a4-page {
    width: ${SHEET_CANVAS_CAPTURE_WIDTH_PX}px !important;
    height: ${SHEET_CANVAS_CAPTURE_HEIGHT_PX}px !important;
    max-width: none !important;
    aspect-ratio: 210 / 297 !important;
    overflow: hidden !important;
    box-shadow: none !important;
    background: #ffffff !important;
    border-radius: 0 !important;
  }
  .cp-program-slide-canvas--a4 {
    width: 100% !important;
    height: 100% !important;
    max-width: none !important;
  }
  .cp-program-slide-canvas-toolbar-wrap,
  .cp-program-slide-canvas-toolbar-wrap--right {
    display: none !important;
  }
`;

const EVALUATION_PLAN_SHEET_CANVAS_PRINT_IMAGE_CSS = `
  @page {
    size: ${A4_WIDTH_MM}mm ${A4_HEIGHT_MM}mm;
    margin: 0;
  }
  html,
  body {
    margin: 0;
    padding: 0;
    background: #ffffff;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  img {
    display: block;
    width: ${A4_WIDTH_MM}mm;
    height: ${A4_HEIGHT_MM}mm;
    margin: 0;
    page-break-inside: avoid;
    break-inside: avoid;
  }
`;

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function isHtml2CanvasSafeCssValue(value: string): boolean {
  const lower = value.toLowerCase();
  return !lower.includes('color-mix') && !/color\s*\(/.test(lower);
}

const SHEET_CANVAS_CAPTURE_STYLE_PROPS = [
  'display',
  'position',
  'top',
  'left',
  'right',
  'bottom',
  'width',
  'height',
  'min-width',
  'min-height',
  'max-width',
  'max-height',
  'z-index',
  'transform',
  'transform-origin',
  'box-sizing',
  'overflow',
  'overflow-x',
  'overflow-y',
  'flex',
  'flex-direction',
  'flex-wrap',
  'flex-grow',
  'flex-shrink',
  'flex-basis',
  'align-items',
  'justify-content',
  'align-self',
  'gap',
  'padding-top',
  'padding-right',
  'padding-bottom',
  'padding-left',
  'margin-top',
  'margin-right',
  'margin-bottom',
  'margin-left',
  'font-weight',
  'font-size',
  'font-family',
  'font-style',
  'line-height',
  'text-align',
  'vertical-align',
  'white-space',
  'word-break',
  'letter-spacing',
  'border-top-width',
  'border-top-style',
  'border-right-width',
  'border-right-style',
  'border-bottom-width',
  'border-bottom-style',
  'border-left-width',
  'border-left-style',
  'border-radius',
  'opacity',
  'visibility',
  'color',
  'background-color',
  'border-top-color',
  'border-right-color',
  'border-bottom-color',
  'border-left-color',
  'text-decoration-line',
  'text-decoration-color',
  'object-fit',
  'pointer-events',
  'user-select',
] as const;

function inlineSheetCanvasCaptureStyles(liveRoot: HTMLElement, cloneRoot: HTMLElement): void {
  const liveNodes: Element[] = [liveRoot, ...Array.from(liveRoot.querySelectorAll('*'))];
  const cloneNodes: Element[] = [cloneRoot, ...Array.from(cloneRoot.querySelectorAll('*'))];
  const count = Math.min(liveNodes.length, cloneNodes.length);

  for (let index = 0; index < count; index += 1) {
    const source = liveNodes[index];
    const target = cloneNodes[index];
    if (!(source instanceof HTMLElement) || !(target instanceof HTMLElement)) continue;

    const computed = window.getComputedStyle(source);
    for (const prop of SHEET_CANVAS_CAPTURE_STYLE_PROPS) {
      const value = computed.getPropertyValue(prop);
      if (!value) continue;
      if (prop.includes('color') && !isHtml2CanvasSafeCssValue(value)) continue;
      target.style.setProperty(prop, value);
    }

    const backgroundImage = computed.getPropertyValue('background-image');
    if (backgroundImage && backgroundImage !== 'none' && isHtml2CanvasSafeCssValue(backgroundImage)) {
      target.style.setProperty('background-image', backgroundImage);
      const backgroundSize = computed.getPropertyValue('background-size');
      if (backgroundSize && backgroundSize !== 'auto') {
        target.style.setProperty('background-size', backgroundSize);
      }
    }

    target.style.setProperty('box-shadow', 'none', 'important');
    target.style.setProperty('text-shadow', 'none', 'important');
  }

  cloneRoot.querySelectorAll('.cp-program-slide-canvas-toolbar-wrap').forEach((el) => el.remove());
}

function getSheetCanvasPages(root: HTMLElement): HTMLElement[] {
  const pages = Array.from(root.querySelectorAll('.cp-evaluation-plan-sheet-canvas-a4-page'));
  if (!pages.length) {
    throw new Error('보낼 평가지 캔버스가 없습니다.');
  }
  return pages as HTMLElement[];
}

function cloneSheetCanvasPage(page: HTMLElement): HTMLElement {
  const clone = page.cloneNode(true) as HTMLElement;
  clone.querySelectorAll('.cp-program-slide-canvas-toolbar-wrap').forEach((el) => el.remove());
  clone.style.width = `${SHEET_CANVAS_CAPTURE_WIDTH_PX}px`;
  clone.style.height = `${SHEET_CANVAS_CAPTURE_HEIGHT_PX}px`;
  clone.style.maxWidth = 'none';
  clone.style.boxShadow = 'none';
  clone.style.borderRadius = '0';
  return clone;
}

async function captureSheetCanvasPage(livePage: HTMLElement): Promise<HTMLCanvasElement> {
  const host = document.createElement('div');
  host.setAttribute('aria-hidden', 'true');
  host.style.cssText =
    'position:fixed;left:-100000px;top:0;z-index:-1;width:794px;background:#fff;overflow:visible;pointer-events:none;';

  const style = document.createElement('style');
  style.textContent = EVALUATION_PLAN_SHEET_CANVAS_PRINT_CSS;
  host.appendChild(style);

  const port = document.createElement('div');
  port.className = 'cp-evaluation-plan-sheet-canvas-print-port';
  const clonedPage = cloneSheetCanvasPage(livePage);
  port.appendChild(clonedPage);
  host.appendChild(port);
  document.body.appendChild(host);

  try {
    await new Promise((resolve) => setTimeout(resolve, DOCUMENT_EXPORT_CAPTURE_SETTLE_MS));

    return await html2canvas(clonedPage, {
      backgroundColor: '#ffffff',
      scale: 2,
      useCORS: true,
      logging: false,
      onclone: (clonedDoc, clonedElement) => {
        applyLightThemeToCloneDocument(clonedDoc);
        stripDocumentExportStylesheetsFromClone(clonedDoc);
        injectStylesIntoCloneDocument(clonedDoc, EVALUATION_PLAN_SHEET_CANVAS_PRINT_CSS);
        inlineSheetCanvasCaptureStyles(livePage, clonedElement);
        stripEditorOnlySheetDecorations(clonedElement);
        rescaleSheetCodeStaticBlocks(clonedElement);
        moveHtml2canvasCloneOffScreen(clonedDoc, clonedElement);
      },
    });
  } finally {
    host.remove();
  }
}

/** 소스코드 블럭 정적 HTML: 캡처 폭(794px)에 맞춰 축척 다시 계산 (화면 표시 축척이 복제본에 남아 있으므로) */
function rescaleSheetCodeStaticBlocks(root: HTMLElement): void {
  root.querySelectorAll<HTMLElement>('.cp-sheet-code-static').forEach((host) => {
    const inner = host.querySelector<HTMLElement>('.cp-sheet-code-static-inner');
    if (!inner) return;
    const designW = parseFloat(inner.style.width) || 0;
    const w = host.clientWidth || host.getBoundingClientRect().width;
    if (!designW || !w) return;
    const scale = w / designW;
    inner.style.transform = `scale(${scale})`;
    inner.style.height = `${100 / scale}%`;
  });
}

/** 편집 화면 전용 표시(빈 투명 소스코드 블럭 빗금) 제거 — 인쇄·PDF·캡처에는 넣지 않음 */
function stripEditorOnlySheetDecorations(root: HTMLElement): void {
  root.querySelectorAll<HTMLElement>('.is-code-empty-hatch').forEach((el) => {
    el.classList.remove('is-code-empty-hatch');
    el.style.setProperty('background', 'transparent', 'important');
    el.querySelectorAll<HTMLElement>('.cp-program-slide-canvas-image-placeholder').forEach((ph) => {
      ph.style.setProperty('background', 'transparent', 'important');
    });
  });
}

async function captureSheetCanvasPages(root: HTMLElement): Promise<HTMLCanvasElement[]> {
  const pages = getSheetCanvasPages(root);
  const captures: HTMLCanvasElement[] = [];
  for (const page of pages) {
    captures.push(await captureSheetCanvasPage(page));
  }
  return captures;
}

/** PDF·프린트와 동일한 html2canvas 캡처로 인쇄 */
export async function printEvaluationPlanSheetCanvasFromRoot(root: HTMLElement, title: string): Promise<void> {
  const canvases = await captureSheetCanvasPages(root);
  const imgTags = canvases
    .map((canvas) => {
      const imgData = canvas.toDataURL('image/jpeg', 0.92);
      return `<img src="${imgData}" alt="" />`;
    })
    .join('\n');
  const printHtml = `<!DOCTYPE html>
<html lang="ko">
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(title)}</title>
    <style>${EVALUATION_PLAN_SHEET_CANVAS_PRINT_IMAGE_CSS}</style>
  </head>
  <body>
    ${imgTags}
  </body>
</html>`;
  printStandaloneHtmlDocument(printHtml);
}

export async function saveEvaluationPlanSheetCanvasAsPdfFromRoot(params: {
  root: HTMLElement;
  fileName: string;
}): Promise<void> {
  const { root, fileName } = params;
  const canvases = await captureSheetCanvasPages(root);
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  canvases.forEach((canvas, index) => {
    if (index > 0) pdf.addPage();
    const imgData = canvas.toDataURL('image/jpeg', 0.92);
    pdf.addImage(imgData, 'JPEG', 0, 0, A4_WIDTH_MM, A4_HEIGHT_MM);
  });
  pdf.save(fileName);
}
